// eslint-disable-next-line no-use-before-define
import { gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { WithApolloClient, withApollo } from '@apollo/client/react/hoc';
import lodash from 'lodash';
import { Component, Fragment } from 'react';

import { RouteComponentProps } from 'react-router';

import { BASE_REGISTRATION_PAGES } from 'op-enums';
import { OPUser, PatientBasic } from 'op-interfaces';
import { SavingStatus } from 'shared-components/enums';
import { ListData } from 'shared-components/interfaces';

import { ExtendLock } from 'op-components';
import { LoadingSpinner } from 'shared-components/components';

import withRegistrationForm, { GET_APOLLO_CACHE, WithRegistrationForm } from '../RegistrationForm';
import RegistrationBasic from './RegistrationBasic';

export const PATIENT_AND_USER_DETAILS_QUERY = gql`
  query PatientAndUserDetails($id: ID!) {
    patient(id: $id) {
      id
      lock {
        lockedBy
        readOnly
        lockedByName
      }
      address {
        id
        formattedAddress
        ukFormattedAddress
      }
      lastVisitedSection
      regFormStatus
      firstName
      middleName
      lastName
      ida
      idb
      gender
      dob
      isProd
      namePrefix
      preferredName
      dobRawDay
      dobRawMonth
      dobRawYear
      primaryCenter
      nhsOptions
      idbConflict
      registrationReason
      registrationReasonText
    }
    nameTitle: listData(category: "nameTitle") {
      id
      name
    }
    user {
      id
      isPso
    }
    genderRefData: listData(category: "gender") {
      id
      name
    }
    locationsRefData: configs {
      id
      name
    }
    nhsOptionsRefData: listData(category: "nhsOptions") {
      id
      name
    }
    registrationReasonRefData: listData(category: "registrationReason") {
      id
      name
    }
  }
`;

interface State {
  saveStatus: SavingStatus;
  pageViewed: boolean;
}

interface PatientAndUserDetailsQueryData {
  patient: PatientBasic;
  nhsOptionsRefData: ListData[];
  nameTitle: ListData[];
  genderRefData: ListData[];
  locationsRefData: ListData[];
  registrationReasonRefData: ListData[];
  user: OPUser;
}

interface Props
  extends WithApolloClient<{}>,
    WithRegistrationForm,
    RouteComponentProps<{
      patientId?: string;
    }> {}

class RegistrationBasicApollo extends Component<Props, State> {
  private userIsPSO = false;

  public constructor(props: Props) {
    super(props);
    this.state = { saveStatus: SavingStatus.SAVED, pageViewed: false };
  }

  public componentDidMount(): void {
    const { client } = this.props;
    let viewedPages: string[] = [];

    // Get the apollo cache values
    this.getApolloCache().then((apolloCache): void => {
      const currentPendingSaveCount = apolloCache.currentPendingSaveCount;
      const saveErrorCount = apolloCache.saveErrorCount;
      const registrationPagesViewed = [...apolloCache.registrationPagesViewed];
      viewedPages = registrationPagesViewed;
      this.setState({
        saveStatus: this.props.getSaveStatus(currentPendingSaveCount, saveErrorCount),
        pageViewed: registrationPagesViewed.includes(BASE_REGISTRATION_PAGES.BASIC),
      });

      if (!viewedPages.includes(BASE_REGISTRATION_PAGES.BASIC)) {
        viewedPages = [...viewedPages, BASE_REGISTRATION_PAGES.BASIC];
      }

      client &&
        client.writeQuery({
          query: gql`
            query {
              registrationPagesViewed
            }
          `,
          data: {
            registrationPagesViewed: viewedPages,
          },
        });
    });
  }

  public render(): JSX.Element {
    const { match, showModalIfLocked } = this.props;
    const { patientId } = match.params;
    return (
      <Query<PatientAndUserDetailsQueryData>
        query={PATIENT_AND_USER_DETAILS_QUERY}
        variables={{ id: patientId }}
        onCompleted={(data: { patient: PatientBasic }): void => {
          showModalIfLocked(data);
        }}>
        {({ loading, data, refetch }): JSX.Element => {
          if (loading) {
            return <LoadingSpinner />;
          }

          if (data && data.patient) {
            // We have a patient - render the form
            const modifiedPatient = { ...data.patient };
            this.userIsPSO = data.user.isPso || false;

            // Check if the patient title is within the title reference data only by id
            const patientTitle = data.patient.namePrefix;
            const foundTitle = data.nameTitle.findIndex((nameTitleItem: ListData) => {
              return nameTitleItem.id === patientTitle;
            });

            // Set the name prefix to empty string if the title is not within the reference data
            if (foundTitle < 0) {
              modifiedPatient.namePrefix = '';
            }

            return (
              <Fragment>
                <ExtendLock accessPatientId={data.patient.id} />
                <RegistrationBasic
                  autosave={this.autosave}
                  patient={modifiedPatient}
                  nhsOptionsRefData={data.nhsOptionsRefData}
                  locationsRefData={data.locationsRefData}
                  genderRefData={data.genderRefData}
                  titleReferenceData={data.nameTitle}
                  registrationReasonRefData={this.formartRegistrationReasonListData(data.registrationReasonRefData)}
                  saveStatus={this.state.saveStatus}
                  validateOnLoad={this.state.pageViewed}
                  isPso={this.userIsPSO}
                  forceRefetch={refetch}
                />
              </Fragment>
            );
          }

          // If we get here there is an error
          return <div>Error</div>;
        }}
      </Query>
    );
  }

  private formartRegistrationReasonListData = (registrationReasonRefData: ListData[]) => {
    return registrationReasonRefData.map((value) => {
      const valueClone = lodash.cloneDeep(value);
      valueClone.id = valueClone.name;
      return valueClone;
    });
  };

  /**
   * Autosave a field and it's values to the database
   * @param patient
   * @param key
   * @param value
   * @param type
   */
  private autosave = async (patient: PatientBasic, key: string, value: string, type = 'String'): Promise<void> => {
    const client = this.props.client;
    const update = {
      key,
      value,
      type,
    };

    const updates = [update];

    if (key === 'idb' && patient.nhsOptions) {
      updates.push({
        key: 'nhsOptions',
        value: !patient.nhsOptions ? '' : patient.nhsOptions,
        type,
      });
    }

    if (key === 'registrationReason') {
      updates.push({
        key: 'registrationReasonText',
        value: '',
        type,
      });
    }

    // Get the pending save count and increment it by 1
    const apolloCache = await this.getApolloCache();
    let currentPendingSaveCount = apolloCache.currentPendingSaveCount + 1;
    let saveErrorCount = apolloCache.saveErrorCount;

    client &&
      client.writeQuery({
        query: gql`
          query {
            pendingSaveCount
          }
        `,
        data: {
          pendingSaveCount: currentPendingSaveCount,
        },
      });
    this.setState({ saveStatus: this.props.getSaveStatus(currentPendingSaveCount, saveErrorCount) });

    // Save the updated data. Once done deincrement the pending save count. If any erros saving increment the saveErrorCount
    return (
      client
        // @ts-ignore
        ?.mutate(this.props.getPatientMutation(patient, updates))
        .then((result): void => {
          if (result.data.updatePatient.errors) {
            saveErrorCount++;
          }
        })
        .catch((): void => {
          this.props.showSavingErrorModal(this.userIsPSO, this.props.history.push);
          saveErrorCount++;
        })
        .finally(async (): Promise<void> => {
          // Reload the save count incase it was set elsewhere during the run of this function
          const apolloCache = await this.getApolloCache();
          currentPendingSaveCount = apolloCache.currentPendingSaveCount - 1;
          client &&
            client.writeQuery({
              query: gql`
                query {
                  pendingSaveCount
                  saveErrorCount
                }
              `,
              data: {
                pendingSaveCount: currentPendingSaveCount,
                saveErrorCount: saveErrorCount,
              },
            });
          this.setState({ saveStatus: this.props.getSaveStatus(currentPendingSaveCount, saveErrorCount) });
        })
    );
  };

  private getApolloCache = async (): Promise<{
    currentPendingSaveCount: number;
    saveErrorCount: number;
    registrationPagesViewed: string[];
  }> => {
    const { client } = this.props;
    try {
      const apolloCache = await client?.query({ query: GET_APOLLO_CACHE });
      const currentPendingSaveCount = apolloCache?.data.pendingSaveCount;
      const saveErrorCount = apolloCache?.data.saveErrorCount;
      const registrationPagesViewed = apolloCache?.data.registrationPagesViewed;
      return {
        currentPendingSaveCount: currentPendingSaveCount,
        saveErrorCount: saveErrorCount,
        registrationPagesViewed: registrationPagesViewed,
      };
    } catch (error) {
      throw error;
    }
  };
}

const apolloComponent = withApollo<Props>(RegistrationBasicApollo);
const component = withRegistrationForm(apolloComponent);
export default component;
